import { CancellationToken } from "@multiweb/types/multiweb";
import { ClassItemRightTypes, ClassItemType } from "../../../OW.Remoting/Remoting/Rights";
import { IClassItemRights } from "./IClassItemRights";

/**
 * Kontext, vzhledem ke kterému se vyhodnocují práva. Typicky instance aplikačního záznamu.
 * @includeToDoc
 */
export interface IRightsContext {
    /** Unikátní identifikátor */
    id: any;
    /** Unikátní identifikátor instance */
    instanceID?: string;
    /** Příznak, zda jde o zrovna vytvářený kontext */
    readonly isNew: boolean;
    /**
     * Příznak, zda je povolená editace
     */
    readonly canEdit: boolean;
    /**
     * Příznak, zda je povoleno mazání
     */
    readonly canDelete: boolean;
    /**
     * Identifikátory rolí, pod kterými může uživatel přistupovat ke kontextu
     */
    readonly roleIds: string[];
    /**
     * Identifikátory rolí, pod kterými může uživatel kontext editovat
     */
    readonly editRoleIds: string[];
    /**
     * Identifikátory rolí, pod kterými může uživatel kontext mazat
     */
    readonly deleteRoleIds: string[];
    readonly isClientSideEncrypted: boolean;
    /**
     * Zjistí práva na zadanou položku kontextu na úrovni aplikačním serveru
     * @param itemName Název položky, pro kterou se zjišťují práva. Typicky atribut nebo metoda.
     * @param type Typ položky
     * @param evalRights Požadované právo k vyhodnocení
     * @param cancellationToken Viz {@link CancellationToken}
     */
    itemRights(itemName: string, type: ClassItemType, evalRights: ClassItemRightTypes, cancellationToken?: CancellationToken): Promise<IClassItemRights>;
    /**
     * Vyhodnotí podmínky nad kontextem na základě Id podmínky
     * @param conditionID Id podmínky
     * @param cancellationToken Viz {@link CancellationToken}
     */
    evaluateCondition(conditionID: string, cancellationToken?: CancellationToken): Promise<boolean>;
}

export class IRightsContext {
    private static _properties: (keyof IRightsContext)[] = ["id", "isNew", "canDelete", "canEdit", "roleIds", "editRoleIds", "deleteRoleIds", "isClientSideEncrypted"];
    private static _methods: (keyof IRightsContext)[] = ["itemRights", "evaluateCondition"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && typeof instance === "object" && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

/**
 * Rozhraní pro komponentu, která si sama určuje, jaký kontext se použije
 * pro vyhodnocení práv na ní samotné a pokud se jedná o frame, tak i jeho potomcích.
 */
export interface IComponentWithCustomEvaluationContext {
    /**
     * Vrací kontext pro vyhodnocení práv na komponentě
     */
    getComponentCustomEvaluationContext(): Promise<IRightsContext>;
}

export class IComponentWithCustomEvaluationContext {
    private static _methods: (keyof IComponentWithCustomEvaluationContext)[] = ["getComponentCustomEvaluationContext"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._methods.some(value => typeof instance[value] !== "function");
    }
}
